import { makeAutoObservable, runInAction } from "mobx";

import type { DetailedReportResponse } from "@/lib/types";
import type {
  FetchRecordDetailsUseCase,
  FetchRecordMarkdownUseCase,
  FetchRecordMarkdownOutputDto,
} from "@/application/records/use-cases";
import type { MarkdownEndpoint } from "@/application/records/ports/RecordsGatewayPort";
import { getAuthSessionId } from "@/infrastructure/auth/AuthSessionManager";
import { getRecordDetailErrorMessage } from "@/lib/record-detail-transport";

class RecordDetailsEntry {
  data: DetailedReportResponse | null = null;
  isLoading = false;
  error: string | null = null;
  loadedAt: number | null = null;

  constructor(readonly versionKey: string) {
    makeAutoObservable(this, { versionKey: false }, { autoBind: true });
  }

  setLoading(value: boolean) {
    this.isLoading = value;
  }

  setError(message: string | null) {
    this.error = message;
    if (message) {
      this.loadedAt = Date.now();
    }
  }

  setData(data: DetailedReportResponse) {
    this.data = data;
    this.loadedAt = Date.now();
  }
}

class MarkdownEntry {
  markdown: string | null = null;
  isLoading = false;
  error: string | null = null;
  version: number | null = null;
  loadedAt: number | null = null;

  constructor(readonly cacheKey: string) {
    makeAutoObservable(this, { cacheKey: false }, { autoBind: true });
  }

  setLoading(value: boolean) {
    this.isLoading = value;
  }

  setError(message: string | null) {
    this.error = message;
    if (message) {
      this.loadedAt = Date.now();
    }
  }

  setValue(result: FetchRecordMarkdownOutputDto) {
    this.markdown = result.markdown;
    this.version = result.version;
    this.loadedAt = Date.now();
  }
}

export interface RecordDetailsStoreDependencies {
  recordId: string;
  fetchDetailsUseCase: FetchRecordDetailsUseCase;
  fetchMarkdownUseCase: FetchRecordMarkdownUseCase;
}

export class RecordDetailsStore {
  private readonly recordId: string;
  private readonly fetchDetailsUseCase: FetchRecordDetailsUseCase;
  private readonly fetchMarkdownUseCase: FetchRecordMarkdownUseCase;

  private readonly detailsEntries = new Map<string, RecordDetailsEntry>();
  private readonly markdownEntries = new Map<string, MarkdownEntry>();

  constructor(deps: RecordDetailsStoreDependencies) {
    this.recordId = deps.recordId;
    this.fetchDetailsUseCase = deps.fetchDetailsUseCase;
    this.fetchMarkdownUseCase = deps.fetchMarkdownUseCase;

    makeAutoObservable(this, {}, { autoBind: true });
  }

  getDetailsEntry(version?: number | null): RecordDetailsEntry {
    const key = this.toVersionKey(version);
    let entry = this.detailsEntries.get(key);
    if (!entry) {
      entry = new RecordDetailsEntry(key);
      this.detailsEntries.set(key, entry);
    }
    return entry;
  }

  getMarkdownEntry(
    reportType: MarkdownEndpoint,
    version?: number | null
  ): MarkdownEntry {
    const key = this.toMarkdownKey(reportType, version);
    let entry = this.markdownEntries.get(key);
    if (!entry) {
      entry = new MarkdownEntry(key);
      this.markdownEntries.set(key, entry);
    }
    return entry;
  }

  async loadDetails(
    version?: number | null,
    options?: { force?: boolean; background?: boolean }
  ): Promise<void> {
    const entry = this.getDetailsEntry(version);
    // Skip when already in-flight or successfully/failed cached unless forced.
    // Force must not bypass the in-flight guard — otherwise refresh storms stack
    // concurrent GETs for the same record (seen after pipeline Finished).
    if (entry.isLoading) {
      return;
    }
    if (!options?.force && entry.loadedAt !== null) {
      return;
    }

    entry.setLoading(true);
    entry.setError(null);

    const requestSessionId = getAuthSessionId();

    try {
      const response = await this.fetchDetailsUseCase.execute({
        recordId: this.recordId,
        version: version ?? null,
        requestContext: {
          scope: "private",
          recordId: this.recordId,
          version: version ?? null,
          isBackgroundRequest: options?.background ?? false,
        },
      });

      runInAction(() => {
        if (requestSessionId !== getAuthSessionId()) {
          return;
        }
        entry.setData(response);
      });
    } catch (error) {
      const message = getRecordDetailErrorMessage(
        error,
        "Failed to load record details"
      );
      runInAction(() => {
        if (requestSessionId !== getAuthSessionId()) {
          return;
        }
        entry.setError(message);
      });
      throw error;
    } finally {
      runInAction(() => {
        if (requestSessionId !== getAuthSessionId()) {
          return;
        }
        entry.setLoading(false);
      });
    }
  }

  async loadMarkdown(
    reportType: MarkdownEndpoint,
    version?: number | null,
    options?: { force?: boolean }
  ): Promise<void> {
    const entry = this.getMarkdownEntry(reportType, version);
    // Skip when already in-flight or successfully/failed cached unless forced.
    // Without this, callers that re-trigger load on every render cause request storms.
    // Force must not bypass the in-flight guard — concurrent force refreshes stack.
    if (entry.isLoading) {
      return;
    }
    if (!options?.force && entry.loadedAt !== null) {
      return;
    }

    entry.setLoading(true);
    entry.setError(null);

    const requestSessionId = getAuthSessionId();

    try {
      const response = await this.fetchMarkdownUseCase.execute({
        recordId: this.recordId,
        reportType,
        version: version ?? null,
      });

      runInAction(() => {
        if (requestSessionId !== getAuthSessionId()) {
          return;
        }
        entry.setValue(response);
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : `Failed to load ${reportType} markdown`;
      runInAction(() => {
        if (requestSessionId !== getAuthSessionId()) {
          return;
        }
        entry.setError(message);
      });
      throw error;
    } finally {
      runInAction(() => {
        if (requestSessionId !== getAuthSessionId()) {
          return;
        }
        entry.setLoading(false);
      });
    }
  }

  reset(): void {
    this.detailsEntries.clear();
    this.markdownEntries.clear();
  }

  private toVersionKey(version?: number | null): string {
    if (version === undefined || version === null) {
      return "latest";
    }
    return String(version);
  }

  private toMarkdownKey(
    reportType: MarkdownEndpoint,
    version?: number | null
  ): string {
    return `${reportType}:${this.toVersionKey(version)}`;
  }
}
